import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

const API = "/api";

function StudentLogin() {
  const navigate = useNavigate();
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window === "undefined" ? 1440 : window.innerWidth));
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState("");
  const [userCaptcha, setUserCaptcha] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isMobile = useMemo(() => viewportWidth <= 768, [viewportWidth]);

  const generateCaptcha = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "";
    for (let i = 0; i < 5; i += 1) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(result);
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(""), 3500);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    const student = localStorage.getItem("student");
    if (student) {
      navigate("/student-dashboard", { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (userCaptcha.trim().toUpperCase() !== captcha) {
      setError("Captcha incorrect");
      generateCaptcha();
      setUserCaptcha("");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const res = await axios.post(`${API}/student-login`, {
        login_id: loginId,
        password,
      });

      if (res.data?.status === "success") {
        localStorage.setItem("student", JSON.stringify(res.data.user));
        navigate("/student-dashboard");
        return;
      }

      setError("Invalid login credentials");
    } catch (err) {
      setError(err.response?.data?.message || "Student login failed");
      generateCaptcha();
      setUserCaptcha("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "radial-gradient(circle at top left, #dbeafe 0%, #f8fafc 40%, #fff7ed 100%)",
        padding: isMobile ? "20px 14px" : "32px 20px",
      }}
    >
      <div
        style={{
          width: "min(1080px, 100%)",
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1.04fr 0.96fr",
          borderRadius: isMobile ? "24px" : "32px",
          overflow: "hidden",
          background: "rgba(255,255,255,0.92)",
          boxShadow: "0 30px 60px rgba(15, 23, 42, 0.14)",
          border: "1px solid rgba(148, 163, 184, 0.16)",
        }}
      >
        <div
          style={{
            padding: isMobile ? "24px 20px" : "42px 38px",
            background: "linear-gradient(145deg, #4e1b24 0%, #7c2d3a 52%, #c2410c 100%)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "24px",
          }}
        >
          <div>
            <p style={{ margin: 0, fontSize: "12px", letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 800, opacity: 0.82 }}>
              NIDCS Student Panel
            </p>
            <h1 style={{ margin: "14px 0 12px", fontSize: isMobile ? "34px" : "48px", lineHeight: 1.04 }}>
              Welcome to the Student Digital Portal
            </h1>
            <p style={{ margin: 0, lineHeight: 1.8, color: "rgba(255,255,255,0.82)" }}>
              Securely sign in to access your personalized learning dashboard, manage your academic profile, and track your progress. Enter your credentials to stay connected with your courses, certifications, and essential campus resources.
            </p>
          </div>

          <div style={{ display: "grid", gap: "12px" }}>
            {[
              "For Login ID Use Student Registration number",
              "For Password Use your Approved Student password",
              "Panel is fully responsive mobile, tablet, and desktop screens.",
            ].map((item) => (
              <div
                key={item}
                style={{
                  padding: "14px 16px",
                  borderRadius: "18px",
                  background: "rgba(255,255,255,0.12)",
                  border: "1px solid rgba(255,255,255,0.14)",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                }}
              >
                <i className="bi bi-check2-circle" style={{ fontSize: "18px" }}></i>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ padding: isMobile ? "24px 20px" : "42px 38px" }}>
          <div style={{ maxWidth: "440px", margin: "0 auto" }}>
            <h2 style={{ margin: 0, color: "#0f172a" }}>Student Login</h2>
            <p style={{ margin: "10px 0 22px", color: "#64748b", lineHeight: 1.7 }}>
              Enter your Registration number and student password to login.
            </p>

            {error ? (
              <div
                style={{
                  marginBottom: "16px",
                  borderRadius: "16px",
                  padding: "14px 16px",
                  background: "#fff1f2",
                  color: "#9f1239",
                  border: "1px solid rgba(244, 63, 94, 0.18)",
                }}
              >
                {error}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "16px" }}>
              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#334155" }}>Login ID</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Registration Number"
                  value={loginId}
                  onChange={(event) => setLoginId(event.target.value)}
                  required
                  style={{ minHeight: "50px", borderRadius: "14px" }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#334155" }}>Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    className="form-control"
                    placeholder="Password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    style={{ minHeight: "50px", borderRadius: "14px", paddingRight: "48px" }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      border: "none",
                      background: "transparent",
                      color: "#64748b",
                    }}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash-fill" : "bi-eye-fill"}`}></i>
                  </button>
                </div>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: 700, color: "#334155" }}>Captcha</label>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "120px 1fr auto", gap: "10px", alignItems: "center" }}>
                  <div
                    style={{
                      minHeight: "50px",
                      borderRadius: "14px",
                      background: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      letterSpacing: "0.22em",
                      color: "#7c2d3a",
                    }}
                  >
                    {captcha}
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter captcha"
                    value={userCaptcha}
                    onChange={(event) => setUserCaptcha(event.target.value.toUpperCase())}
                    required
                    style={{ minHeight: "50px", borderRadius: "14px" }}
                  />
                  <button
                    type="button"
                    onClick={generateCaptcha}
                    style={{
                      minHeight: "50px",
                      border: "1px solid rgba(124, 45, 58, 0.16)",
                      background: "#fff",
                      color: "#7c2d3a",
                      borderRadius: "14px",
                      padding: "0 16px",
                      fontWeight: 700,
                    }}
                  >
                    <i className="bi bi-arrow-repeat"></i>
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                style={{
                  minHeight: "54px",
                  border: "none",
                  borderRadius: "16px",
                  background: "linear-gradient(135deg, #7c2d3a 0%, #c2410c 100%)",
                  color: "#fff",
                  fontWeight: 800,
                  boxShadow: "0 18px 28px rgba(124, 45, 58, 0.22)",
                }}
              >
                {loading ? "Logging in..." : "Login to Dashboard"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default StudentLogin;
